<template>
  <div class="port-info flex-layout">
    <div class="flex-item">
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.neighbor.card.table.host }} :</div>
        <div class="flex-item left host-item ellipsis" :title="info.hostName">{{ info.hostName }}</div>
      </div>
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.neighbor.card.table.mac_address }} :</div>
        <div class="flex-item left">{{ info.hostMacAddress }}</div>
      </div>
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.neighbor.card.table.ip_address }} :</div>
        <div class="flex-item left">{{ info.hostIpAddress }}</div>
      </div>
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.popover.lldp_remote_id }} :</div>
        <div class="flex-item left">{{ info.remotePortId }}</div>
      </div>
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.popover.received_rate }} :</div>
        <div class="flex-item left">{{ info.bandwidthDownload }} <span v-if="isDownloadWarning"
            class="warning">Warning</span>
        </div>
      </div>
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.popover.transmitted_rate }} :</div>
        <div class="flex-item left">{{ info.bandwidthUpload }} <span v-if="isUploadWarning"
            class="warning">Warning</span>
        </div>
      </div>
      <!--    <div class="flex-layout port-item">
          <div class="item-label left">{{$sakura.locale.popover.total_packets}} :</div>
          <div class="flex-item left">{{info.trafficUsage}}</div>
        </div>-->
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.popover.power_usage }} :</div>
        <div class="flex-item left">{{ w(info.powerUsage) }} Watts</div>
      </div>
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.popover.port_type }} :</div>
        <div class="flex-item left">{{ info.portType }}</div>
      </div>
      <div class="flex-layout port-item">
        <div class="item-label left">{{ $sakura.locale.popover.link_speed }} :</div>
        <div class="flex-item left">{{ info.linkSpeed }}</div>
      </div>
      <div class="flex-layout port-item more-vlan-data">
        <div class="item-label left">VLAN ID :</div>
        <div class="flex-item left" v-if="info">
          <span v-if="info.vlan">{{ Array.from(new Set(info.vlan)).join(',') }}</span>
        </div>
      </div>
      <template v-if="showProfile">
        <div class="flex-layout port-item more-vlan-data">
          <div class="item-label left">{{ $sakura.locale.popover.profile_name }} :</div>
          <div class="flex-item left host-item">{{ info.profileName }}</div>
        </div>
        <div class="flex-layout port-item">
          <div class="item-label left">{{ $sakura.locale.popover.profile_template }} :</div>
          <div class="flex-item left host-item">{{ templates(info.profileType) }}</div>
        </div>
      </template>
    </div>
    <div class="img-item trans" v-show="!!img">
      <img :src="img" alt="" v-if="img" />
    </div>
  </div>
</template>

<script setup>
import { inject, ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
// import { useStore } from 'vuex'
import { useDeviceStore, useNetworksStore, useMainStore, useNeighborStore } from '@/stores';
import { templates, showData } from "@/js/filters";


const networksStore = useNetworksStore();

const props = defineProps({
  port: {
    type: Number,
    default: 0
  },
  showProfile: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:transmitted', 'update:received'])

const network = inject('network', null)

const info = ref({})
const timer = ref(null)
const img = ref(null)

const portList = computed(() => {
  return networksStore.portList
})

const profileName = () => {
  const node = network?.portInterfaceMap?.get(props.port)
  return node?.profileName || ''
}

const profileType = () => {
  const node = network?.portInterfaceMap?.get(props.port)
  return node?.profileTemplate || ''
}

const isUploadWarning = computed(() => {
  const percent = info.value.percetUtilizationTransmitted
  const status = percent >= 95
  emit('update:transmitted', !!percent && status)
  return !!percent && status
})

const isDownloadWarning = computed(() => {
  const percent = info.value.percetUtilizationReceived
  const status = percent >= 95
  emit('update:received', !!percent && status)
  return !!percent && status
})

const checkPortList = () => {
  const arr = portList.value.filter(item => item.portNum === props.port);
  if (arr.length) {
    info.value = {
      ...arr[0],
      profileName: profileName(),
      profileType: profileType()
    }
  }
}

watch(
  () => networksStore.portList, // 👈 watcher source
  () => {
    checkPortList();
  },
  { immediate: true, deep: true } // run immediately on mounted + watch deeply
)

const init = () => {
  checkPortList();
}
function w(value) {
  return value ? parseFloat((value / 1000).toFixed(0)) : 0;
}

const createImg = () => {
  const image = new Image()
  image.src = `http://${info.value.hostIpAddress}/snapshot.jpg?${Date.now()}`
  image.crossOrigin = 'Anonymous'
  image.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = image.width
    canvas.height = image.height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(image, 0, 0)
    img.value = canvas.toDataURL('image/jpg')
  }
  image.onerror = () => {
    img.value = null
  }
}

const open = () => {
  init()
  const profile = info.value.profileType
  const mac = info.value.hostMacAddress || ''
  const isHarman = typeof window.$sakura?.isHarman === 'function' 
    ? window.$sakura.isHarman(profile)
    : false

  if (isHarman && mac.startsWith('00:19:0B')) {
    createImg()
    timer.value = setInterval(createImg, 5000)
  }
}

const close = () => {
  if (timer.value) clearInterval(timer.value)
  info.value = {}
  img.value = null
  timer.value = null
}

watch(() => portList.value, init, { deep: true })
watch(() => props.port, init)

onMounted(() => {
  init()
})

onBeforeUnmount(() => {
  if (timer.value) clearInterval(timer.value)
})

defineExpose({
  open,
  close
})
</script>



<style scoped>
.port-item {
  margin: 8px 0;
}

.item-label {
  width: 186px;
}

.warning {
  color: #e6a23c;
}

.host-item {
  overflow: hidden;
  white-space: nowrap;
}

.more-vlan-data {
  max-width: 400px;
  max-height: 150px;
  overflow: auto;
}
</style>
